import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Switch,
  Stack,
  Divider,
  Chip,
  Grid,
  IconButton,
} from '@mui/joy';
import {
  Gear as SettingsIcon,
  ThumbsUp as ThumbUpIcon,
  ThumbsDown as ThumbDownIcon,
  Copy as CopyIcon,
  ArrowSquareOut as OpenInNewIcon,
  QrCode as QrCodeIcon,
  DownloadSimple as DownloadIcon,
} from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import QRCode from 'react-qr-code';
import { api } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';

interface ReviewFunnel {
  id?: number;
  is_active: boolean;
  name: string;
  intro_message: string;
  feedback_form_title: string;
  feedback_form_message: string;
  feedback_thank_you: string;
  primary_color: string;
  collection_title: string;
  collection_logo_url: string;
  project_image_url: string;
  project_image_caption: string;
  available_platforms: number[];
  public_url?: string;
}

interface ReviewPlatform {
  id: number;
  platform: string;
  account_name: string;
  is_active: boolean;
  review_url?: string;
}

export const ReviewFunnel: React.FC = () => {
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedQr, setCopiedQr] = useState(false);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  // Fetch funnel configuration
  const { data: funnel, isLoading } = useQuery({
    queryKey: ['review-funnel'],
    queryFn: async () => {
      const response = await api.get<ReviewFunnel>('/api/marketing/review-funnels/get_active/');
      return response.data;
    },
  });

  const publicUrl = useMemo(() => {
    if (funnel?.public_url) return funnel.public_url;
    const slug = (user?.organization as { slug?: string })?.slug
      || user?.organization?.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
      || 'your-company';
    return `${window.location.origin}/reviews/${slug}`;
  }, [funnel, user]);

  const organizationSlug = publicUrl.split('/reviews/')[1] || 'your-company';

  // Fetch available platforms
  const { data: platforms = [] } = useQuery({
    queryKey: ['review-platforms-active'],
    queryFn: async () => {
      const response = await api.get<{ results: ReviewPlatform[] }>('/api/marketing/review-platforms/?is_active=true');
      return response.data.results || [];
    },
  });

  // Form state
  const [formData, setFormData] = useState<ReviewFunnel>({
    is_active: true,
    name: 'Default Review Funnel',
    intro_message: 'Would you mind sharing your experience with others? Your review helps us grow and serve more customers like you.',
    feedback_form_title: 'We appreciate your feedback',
    feedback_form_message: 'We\'re sorry your experience didn\'t meet expectations. Please share your feedback so we can improve.',
    feedback_thank_you: 'Thank you for your feedback! We truly appreciate you taking the time to share your experience.',
    primary_color: '#2563eb',
    collection_title: 'Share your experience',
    collection_logo_url: '',
    project_image_url: '',
    project_image_caption: '',
    available_platforms: [],
  });

  useEffect(() => {
    if (funnel) {
      setFormData(funnel);
    }
  }, [funnel]);

  // Save funnel configuration
  const saveFunnel = useMutation({
    mutationFn: async (data: ReviewFunnel) => {
      if (data.id) {
        const response = await api.patch(`/api/marketing/review-funnels/${data.id}/`, data);
        return response.data;
      } else {
        const response = await api.post('/api/marketing/review-funnels/', data);
        return response.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-funnel'] });
      setConfigModalOpen(false);
    },
  });

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  const handleSave = () => {
    saveFunnel.mutate(formData);
  };

  const handleDownloadQr = () => {
    const svg = document.getElementById('qr-code-svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      
      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = `review-funnel-qr-${organizationSlug}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const handleCopyQr = () => {
    setCopiedQr(true);
    setTimeout(() => setCopiedQr(false), 2000);
  };

  return (
    <Box>
      <Alert color="neutral" variant="soft" sx={{ mb: 3 }}>
        Create a branded collection page where every customer can leave a public review or send private feedback.
      </Alert>

      <Grid container spacing={3}>
        {/* Main Settings Card */}
        <Grid xs={12} lg={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography level="h4">Review Funnel Settings</Typography>
                  {funnel && (
                    <Chip
                      size="sm"
                      color={funnel.is_active ? 'success' : 'neutral'}
                      variant="soft"
                      sx={{ mt: 1 }}
                    >
                      {funnel.is_active ? 'Active' : 'Inactive'}
                    </Chip>
                  )}
                </Box>
                <Button
                  startDecorator={<SettingsIcon />}
                  onClick={() => setConfigModalOpen(true)}
                >
                  Configure Funnel
                </Button>
              </Box>

              <Typography level="body-md" sx={{ color: 'text.secondary', mb: 3 }}>
                The review funnel helps you manage your online reputation by:
              </Typography>
              
              <Box sx={{ pl: 2 }}>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ThumbUpIcon color="var(--joy-palette-success-500)" />
                    <Typography level="body-md">
                      Every customer can choose a public review platform
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ThumbDownIcon color="var(--joy-palette-danger-500)" />
                    <Typography level="body-md">
                      Every customer can share private feedback directly with your team
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box>
                <Typography level="title-md" sx={{ mb: 2 }}>Public Review Collection URL</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Input
                    value={publicUrl}
                    readOnly
                    sx={{ flex: 1 }}
                    endDecorator={
                      <Stack direction="row" spacing={0.5}>
                        <IconButton
                          size="sm"
                          variant="plain"
                          onClick={handleCopyUrl}
                        >
                          <CopyIcon />
                        </IconButton>
                        <IconButton
                          size="sm"
                          variant="plain"
                          component="a"
                          href={publicUrl}
                          target="_blank"
                        >
                          <OpenInNewIcon />
                        </IconButton>
                      </Stack>
                    }
                  />
                </Box>
                {copiedUrl && (
                  <Typography level="body-xs" sx={{ color: 'success.main', mt: 1 }}>
                    URL copied to clipboard!
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Stats Card */}
        <Grid xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography level="title-md" sx={{ mb: 2 }}>Funnel Performance</Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    Total Views
                  </Typography>
                  <Typography level="h3">0</Typography>
                </Box>
                <Box>
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    Conversion Rate
                  </Typography>
                  <Typography level="h3">0%</Typography>
                </Box>
                <Box>
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    Reviews Generated
                  </Typography>
                  <Typography level="h3">0</Typography>
                </Box>
              </Stack>
              
              <Divider sx={{ my: 2 }} />
              
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startDecorator={<QrCodeIcon />}
                onClick={() => setQrModalOpen(true)}
              >
                Generate QR Code
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Configuration Modal */}
      <Modal open={configModalOpen} onClose={() => setConfigModalOpen(false)}>
        <ModalDialog sx={{ maxWidth: 900, width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 3 }}>
            Configure Review Funnel
          </Typography>

          <Stack spacing={3}>
            {/* Basic Settings */}
            <Box>
              <Typography level="title-md" sx={{ mb: 2 }}>Basic Settings</Typography>
              <Stack spacing={2}>
                <FormControl>
                  <FormLabel>Funnel Name</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Main Review Funnel"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Active Status</FormLabel>
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    slotProps={{
                      track: {
                        children: formData.is_active ? 'Active' : 'Inactive',
                        sx: {
                          display: 'flex',
                          alignItems: 'center',
                          // Keep the label on the opposite side of the thumb
                          justifyContent: formData.is_active ? 'flex-start' : 'flex-end',
                          px: 1,
                          fontSize: 'xs',
                        },
                      },
                    }}
                    sx={{
                      alignSelf: 'flex-start',
                      '--Switch-thumbSize': '18px',
                      '--Switch-trackWidth': '84px',
                      '--Switch-trackHeight': '26px',
                    }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Available Review Platforms</FormLabel>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {platforms.map((platform) => (
                      <Chip
                        key={platform.id}
                        variant={formData.available_platforms.indexOf(platform.id) !== -1 ? 'solid' : 'outlined'}
                        onClick={() => {
                          const newPlatforms = formData.available_platforms.indexOf(platform.id) !== -1
                            ? formData.available_platforms.filter(p => p !== platform.id)
                            : [...formData.available_platforms, platform.id];
                          setFormData({ ...formData, available_platforms: newPlatforms });
                        }}
                      >
                        {platform.account_name}
                      </Chip>
                    ))}
                  </Box>
                </FormControl>
              </Stack>
            </Box>

            <Divider />

            {/* Happy Path Settings */}
            <Box>
              <Typography level="title-md" sx={{ mb: 2 }}>
                Collection page
              </Typography>
              <Stack spacing={2}>
                <FormControl>
                  <FormLabel>Title</FormLabel>
                  <Input
                    value={formData.collection_title}
                    onChange={(e) => setFormData({ ...formData, collection_title: e.target.value })}
                    placeholder="Share your experience"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Message</FormLabel>
                  <Textarea
                    value={formData.intro_message}
                    onChange={(e) => setFormData({ ...formData, intro_message: e.target.value })}
                    placeholder="Would you mind sharing your experience..."
                    minRows={3}
                  />
                </FormControl>
              </Stack>
            </Box>

            <Divider />

            {/* Unhappy Path Settings */}
            <Box>
              <Typography level="title-md" sx={{ mb: 2 }}>
                Private feedback
              </Typography>
              <Stack spacing={2}>
                <FormControl>
                  <FormLabel>Title</FormLabel>
                  <Input
                    value={formData.feedback_form_title}
                    onChange={(e) => setFormData({ ...formData, feedback_form_title: e.target.value })}
                    placeholder="We appreciate your feedback"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Message</FormLabel>
                  <Textarea
                    value={formData.feedback_form_message}
                    onChange={(e) => setFormData({ ...formData, feedback_form_message: e.target.value })}
                    placeholder="We're sorry your experience didn't meet expectations..."
                    minRows={3}
                  />
                </FormControl>
              </Stack>
            </Box>

            <Divider />

            {/* Thank You Message */}
            <Box>
              <Typography level="title-md" sx={{ mb: 2 }}>Completion</Typography>
              <FormControl>
                <FormLabel>Thank You Message</FormLabel>
                <Textarea
                  value={formData.feedback_thank_you}
                  onChange={(e) => setFormData({ ...formData, feedback_thank_you: e.target.value })}
                  placeholder="Thank you for your feedback!"
                  minRows={2}
                />
              </FormControl>
            </Box>

            <Stack gap={2}>
              <FormControl><FormLabel>Brand accent</FormLabel><Input type="color" value={formData.primary_color || '#2563eb'} onChange={e => setFormData({ ...formData, primary_color: e.target.value })} /></FormControl>
              <FormControl><FormLabel>Logo URL (HTTPS, optional)</FormLabel><Input value={formData.collection_logo_url || ''} onChange={e => setFormData({ ...formData, collection_logo_url: e.target.value })} /></FormControl>
              <FormControl><FormLabel>Project image URL (HTTPS, optional)</FormLabel><Input value={formData.project_image_url || ''} onChange={e => setFormData({ ...formData, project_image_url: e.target.value })} /></FormControl>
              <FormControl><FormLabel>Project image description</FormLabel><Input value={formData.project_image_caption || ''} onChange={e => setFormData({ ...formData, project_image_caption: e.target.value })} /></FormControl>
            </Stack>
            {saveFunnel.isError && <Alert color="danger">Unable to save. Check the image URLs and required fields, then retry.</Alert>}
            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button variant="plain" onClick={() => setConfigModalOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                loading={saveFunnel.isPending}
              >
                Save Configuration
              </Button>
            </Box>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* QR Code Modal */}
      <Modal open={qrModalOpen} onClose={() => setQrModalOpen(false)}>
        <ModalDialog sx={{ maxWidth: 500, textAlign: 'center' }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 3 }}>
            QR Code for Review Funnel
          </Typography>
          
          <Box sx={{ 
            p: 4, 
            bgcolor: 'background.level1', 
            borderRadius: 'md',
            mb: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
          }}>
            <Box id="qr-code-svg">
              <QRCode
                value={publicUrl}
                size={256}
                style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                viewBox={`0 0 256 256`}
              />
            </Box>
            
            <Typography level="body-sm" sx={{ color: 'text.secondary', wordBreak: 'break-all' }}>
              {publicUrl}
            </Typography>
          </Box>
          
          <Stack spacing={2}>
            <Button
              fullWidth
              startDecorator={<DownloadIcon />}
              onClick={handleDownloadQr}
            >
              Download QR Code
            </Button>
            
            <Stack direction="row" spacing={1}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startDecorator={<CopyIcon />}
                onClick={() => {
                  navigator.clipboard.writeText(publicUrl);
                  handleCopyQr();
                }}
              >
                Copy URL
              </Button>
              
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                component="a"
                href={publicUrl}
                target="_blank"
                startDecorator={<OpenInNewIcon />}
              >
                Preview
              </Button>
            </Stack>
            
            {copiedQr && (
              <Typography level="body-xs" sx={{ color: 'success.main' }}>
                URL copied to clipboard!
              </Typography>
            )}
          </Stack>
          
          <Typography level="body-xs" sx={{ mt: 3, color: 'text.secondary' }}>
            Scan this QR code with a mobile device to access your review collection funnel
          </Typography>
        </ModalDialog>
      </Modal>
    </Box>
  );
};